import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import emailjs from "@emailjs/browser";
import { buildApiUrl } from "@/lib/config";
import { useToast } from "@/hooks/use-toast";
import { trackEvent, analyticsEvents } from "@/lib/ga4";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Truck, CheckCircle2, ArrowRight, Loader2, Package, MapPin, Home, Upload, FileText, X } from "lucide-react";

// ── Constants ──────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "THB", name: "Thai Baht" },
  { code: "AED", name: "UAE Dirham" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "SAR", name: "Saudi Arabian Riyal" },
  { code: "VND", name: "Vietnamese Dong" },
  { code: "CNY", name: "Chinese Yuan" },
];

const BRANCHES = [
  {
    id: "sarita_vihar",
    name: "Sarita Vihar",
    address: "105, 1st Floor, Pocket-B Market, Sarita Vihar, New Delhi-110076",
    city: "New Delhi",
    state: "Delhi",
    pinCode: "110076",
  },
  {
    id: "barakhamba",
    name: "Barakhamba Road",
    address: "2-P&2-F, 2nd Floor, DCM Building, 16 Barakhamba Road, New Delhi-110001",
    city: "New Delhi",
    state: "Delhi",
    pinCode: "110001",
  },
  {
    id: "vasant_kunj",
    name: "Vasant Kunj",
    address: "C-28, Shop#5, Masoodpur, Vasant Kunj, New Delhi-110070",
    city: "New Delhi",
    state: "Delhi",
    pinCode: "110070",
  },
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const PURPOSE_OPTIONS = [
  "Private Visit",
  "Education",
  "Education Remittance",
  "Business Travel",
  "Medical Treatment",
  "Gift / Remittance",
  "Other",
];

type DocRequirement = { id: string; label: string; required: boolean };

const DOCUMENT_REQUIREMENTS: Record<string, DocRequirement[]> = {
  "Private Visit": [
    { id: "passport_photo", label: "Passport (Photo Page)", required: true },
    { id: "passport_address", label: "Passport (Address Page)", required: true },
    { id: "visa", label: "Visa", required: true },
    { id: "ticket", label: "Travel Ticket", required: true },
    { id: "pan_card", label: "PAN Card", required: true },
  ],
  "Education": [
    { id: "passport", label: "Passport", required: true },
    { id: "visa", label: "Visa", required: true },
    { id: "ticket", label: "Travel Ticket", required: true },
    { id: "pan_card", label: "PAN Card", required: true },
    { id: "offer_letter", label: "University Offer Letter", required: true },
  ],
  "Education Remittance": [
    { id: "passport", label: "Passport", required: true },
    { id: "visa", label: "Visa", required: true },
    { id: "student_pan", label: "Student PAN Card", required: true },
    { id: "remitter_pan", label: "Remitter PAN Card", required: true },
    { id: "offer_letter", label: "University Offer Letter", required: true },
    { id: "bank_details", label: "Bank Details / Statement", required: true },
  ],
};

const REQUIRES_DOCS = new Set(["Private Visit", "Education", "Education Remittance"]);

const EMAIL_CONFIG = {
  serviceId: "service_4pz4dzf",
  templateId: "template_aelz1vr",
  publicKey: "uhHa5ey8ZNE5hSWsN",
  toEmails: "forex@travelwingsonline.in,hr@travelwingsonline.in,it@travelwingsonline.in",
};

// ── Delivery charge logic ──────────────────────────────────────────────────

const getDeliveryCharge = (inrAmount: number): number =>
  inrAmount <= 50000 ? 250 : 150;

// ── GST logic (on INR equivalent) ─────────────────────────────────────────
// ≤ ₹25,000 → ₹45 flat | ₹25,001–₹1,00,000 → 0.18% | > ₹1,00,000 → ₹180 + 0.09%

const getGSTCharge = (inrAmount: number): number => {
  if (inrAmount <= 0) return 0;
  if (inrAmount <= 25000) return 45;
  if (inrAmount <= 100000) return parseFloat((inrAmount * 0.0018).toFixed(2));
  return parseFloat((180 + inrAmount * 0.0009).toFixed(2));
};

// ── Form schema ────────────────────────────────────────────────────────────

const orderSchema = z
  .object({
    fulfillmentType: z.enum(["home_delivery", "branch_pickup"]),
    branchLocation: z.string().min(1, "Please select a branch"),
    currencyCode: z.string().min(1, "Please select a currency"),
    currencyAmount: z.coerce
      .number({ invalid_type_error: "Please enter a valid amount" })
      .positive("Amount must be greater than 0"),
    fullName: z.string().min(2, "Full name is required"),
    mobile: z
      .string()
      .regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number"),
    email: z.string().email("Enter a valid email address"),
    deliveryAddress: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    pinCode: z.string().optional(),
    preferredDeliveryDate: z.string().min(1, "Please select a preferred date"),
    purpose: z.string().optional(),
    notes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.fulfillmentType === "home_delivery") {
      if (!data.deliveryAddress || data.deliveryAddress.length < 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please enter your complete delivery address",
          path: ["deliveryAddress"],
        });
      }
      if (!data.city || data.city.length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "City is required", path: ["city"] });
      }
      if (!data.state || data.state.length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please select your state", path: ["state"] });
      }
      if (!data.pinCode || !/^\d{6}$/.test(data.pinCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid 6-digit PIN code", path: ["pinCode"] });
      }
    }
  });

type OrderFormData = z.infer<typeof orderSchema>;

// ── Props ──────────────────────────────────────────────────────────────────

interface ForexHomeDeliveryModalProps {
  trigger?: React.ReactNode;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ForexHomeDeliveryModal({ trigger }: ForexHomeDeliveryModalProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, { url: string; filename: string }>>({});
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  const { toast } = useToast();

  const minDate = new Date().toISOString().split("T")[0];
  const maxDate = new Date(Date.now() + 90 * 86400000).toISOString().split("T")[0];

  const { data: forexRates = [] } = useQuery<any[]>({
    queryKey: ["/api/forex-rates/today"],
    queryFn: async () => {
      const res = await fetch(buildApiUrl("api/forex-rates/today"));
      if (!res.ok) throw new Error("Failed to fetch rates");
      return res.json();
    },
    enabled: open,
  });

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      fulfillmentType: "home_delivery",
      branchLocation: "",
      currencyCode: "",
      currencyAmount: undefined as any,
      fullName: "",
      mobile: "",
      email: "",
      deliveryAddress: "",
      city: "",
      state: "",
      pinCode: "",
      preferredDeliveryDate: "",
      purpose: "",
      notes: "",
    },
  });

  const watchedCode = form.watch("currencyCode");
  const watchedAmount = form.watch("currencyAmount");
  const fulfillmentType = form.watch("fulfillmentType");
  const isHomeDelivery = fulfillmentType === "home_delivery";
  const watchedPurpose = form.watch("purpose");
  const needsDocs = !!watchedPurpose && REQUIRES_DOCS.has(watchedPurpose);
  const requiredDocs: DocRequirement[] = needsDocs ? (DOCUMENT_REQUIREMENTS[watchedPurpose] ?? []) : [];

  const rateObj = forexRates.find(
    (r: any) => (r.currencyCode || r.currency_code) === watchedCode
  );
  const exchangeRate = rateObj ? parseFloat(rateObj.rate) : 0;
  const currencyAmountNum = parseFloat(String(watchedAmount)) || 0;
  const inrEquivalent = currencyAmountNum * exchangeRate;
  const deliveryCharge = isHomeDelivery && inrEquivalent > 0 ? getDeliveryCharge(inrEquivalent) : 0;
  const gstCharge = inrEquivalent > 0 ? getGSTCharge(inrEquivalent) : 0;
  const totalAmount = inrEquivalent + deliveryCharge + gstCharge;

  const formatINR = (n: number) =>
    n > 0
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "—";

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSubmitted(false);
      setIsSubmitting(false);
      setUploadedDocs({});
      setUploadingDoc(null);
      form.reset();
    }
  }, [open]);

  useEffect(() => {
    setUploadedDocs({});
  }, [watchedPurpose]);

  // When switching fulfillment type, clear fields that don't apply
  const handleFulfillmentChange = (type: "home_delivery" | "branch_pickup") => {
    form.setValue("fulfillmentType", type);
    if (type === "branch_pickup") {
      form.setValue("deliveryAddress", "");
      form.setValue("city", "");
      form.setValue("state", "");
      form.setValue("pinCode", "");
    }
  };

  const handleContinue = async () => {
    const valid = await form.trigger(["currencyCode", "currencyAmount", "branchLocation"]);
    if (!valid) return;
    if (inrEquivalent <= 0 || exchangeRate === 0) {
      toast({
        title: "Rate unavailable",
        description: "Exchange rate for the selected currency is not available today. Please try again later.",
        variant: "destructive",
      });
      return;
    }
    setStep(2);
  };

  const handleDocumentUpload = async (docId: string, file: File) => {
    setUploadingDoc(docId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", docId);
      const res = await fetch(buildApiUrl("api/forex-delivery/documents/upload"), {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const { url, filename } = await res.json();
      setUploadedDocs((prev) => ({ ...prev, [docId]: { url, filename } }));
      toast({ title: "Document uploaded", description: `${file.name} uploaded successfully.` });
    } catch {
      toast({ title: "Upload failed", description: "Failed to upload document. Please try again.", variant: "destructive" });
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleStep2Continue = async () => {
    const fieldsToValidate: (keyof OrderFormData)[] = ["fullName", "mobile", "email", "preferredDeliveryDate"];
    if (isHomeDelivery) {
      fieldsToValidate.push("deliveryAddress", "city", "state", "pinCode");
    }
    const valid = await form.trigger(fieldsToValidate);
    if (!valid) return;
    if (needsDocs) {
      setStep(3);
    } else {
      form.handleSubmit(onSubmit)();
    }
  };

  const buildEmailBody = (data: OrderFormData) => {
    const selectedBranch = BRANCHES.find((b) => b.id === data.branchLocation);
    const lines = [
      "Hello Team,",
      "",
      `A new Foreign Currency ${isHomeDelivery ? "Home Delivery" : "Branch Pickup"} request has been submitted. Details below:`,
      "",
      "=== CUSTOMER DETAILS ===",
      `Full Name : ${data.fullName}`,
      `Mobile    : ${data.mobile}`,
      `Email     : ${data.email}`,
      "",
      "=== ORDER DETAILS ===",
      `Currency  : ${currencyAmountNum.toLocaleString("en-IN")} ${data.currencyCode}`,
      `Rate      : 1 ${data.currencyCode} = ₹${exchangeRate.toFixed(4)} INR`,
      `INR Equiv : ₹${formatINR(inrEquivalent)}`,
      `Delivery  : ${isHomeDelivery ? `₹${deliveryCharge}` : "₹0 (Branch Pickup)"}`,
      `GST       : ₹${formatINR(gstCharge)}`,
      `Total     : ₹${formatINR(totalAmount)}`,
      "",
    ];

    if (isHomeDelivery) {
      lines.push(
        "=== DELIVERY DETAILS ===",
        `Branch    : ${selectedBranch?.name ?? data.branchLocation} (dispatching branch)`,
        `Address   : ${selectedBranch?.address ?? ""}`,
        `Deliver To: ${data.deliveryAddress}`,
        `City      : ${data.city}`,
        `State     : ${data.state}`,
        `PIN Code  : ${data.pinCode}`,
        `Pref Date : ${data.preferredDeliveryDate}`,
      );
    } else {
      lines.push(
        "=== PICKUP DETAILS ===",
        `Branch    : ${selectedBranch?.name ?? data.branchLocation}`,
        `Address   : ${selectedBranch?.address ?? ""}`,
        `Pref Date : ${data.preferredDeliveryDate}`,
      );
    }

    lines.push(
      `Purpose   : ${data.purpose || "Not specified"}`,
      `Notes     : ${data.notes || "None"}`,
      "",
    );

    const docEntries = Object.entries(uploadedDocs);
    if (docEntries.length > 0) {
      lines.push("=== UPLOADED DOCUMENTS ===");
      const allDocs = needsDocs ? requiredDocs : [];
      docEntries.forEach(([docId, { url, filename }]) => {
        const label = allDocs.find((d) => d.id === docId)?.label ?? docId;
        lines.push(`${label}: ${url} (${filename})`);
      });
      lines.push("");
    }

    lines.push(
      `Submitted : ${new Date().toLocaleString("en-IN")}`,
      "Status    : Pending",
      "",
      "Please action this request at the earliest.",
      `Source: Website – Foreign Currency ${isHomeDelivery ? "Home Delivery" : "Branch Pickup"} Form`,
    );

    return lines.join("\n");
  };

  const onSubmit = async (data: OrderFormData) => {
    setIsSubmitting(true);
    try {
      const selectedBranch = BRANCHES.find((b) => b.id === data.branchLocation);

      // For branch pickup, use the branch address as the delivery address
      const resolvedAddress = isHomeDelivery
        ? data.deliveryAddress!
        : `BRANCH PICKUP: ${selectedBranch?.address ?? data.branchLocation}`;
      const resolvedCity = isHomeDelivery ? data.city! : selectedBranch?.city ?? "New Delhi";
      const resolvedState = isHomeDelivery ? data.state! : selectedBranch?.state ?? "Delhi";
      const resolvedPin = isHomeDelivery ? data.pinCode! : selectedBranch?.pinCode ?? "110001";

      const docs = Object.entries(uploadedDocs).map(([type, { url, filename }]) => ({ type, url, filename }));

      const payload = {
        fullName: data.fullName,
        mobile: data.mobile,
        email: data.email,
        currencyCode: data.currencyCode,
        currencyAmount: currencyAmountNum.toFixed(2),
        inrEquivalent: inrEquivalent.toFixed(2),
        exchangeRate: exchangeRate.toFixed(4),
        deliveryCharge,
        gstCharge: gstCharge.toFixed(2),
        totalAmount: totalAmount.toFixed(2),
        deliveryAddress: resolvedAddress,
        city: resolvedCity,
        state: resolvedState,
        pinCode: resolvedPin,
        preferredDeliveryDate: data.preferredDeliveryDate,
        purpose: data.purpose || null,
        notes: isHomeDelivery
          ? [`Dispatching Branch: ${selectedBranch?.name ?? data.branchLocation}`, data.notes].filter(Boolean).join("\n")
          : data.notes || null,
        documents: docs.length > 0 ? docs : null,
      };

      const res = await fetch(buildApiUrl("api/forex-delivery/order"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Backend save failed");

      const nameParts = data.fullName.trim().split(" ");
      await emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.templateId,
        {
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(" ") || "",
          service_interest: isHomeDelivery ? "Foreign Currency Home Delivery" : "Foreign Currency Branch Pickup",
          reply_to: data.email,
          user_email: data.email,
          phone: data.mobile,
          message: buildEmailBody(data),
          to_email: EMAIL_CONFIG.toEmails,
        },
        { publicKey: EMAIL_CONFIG.publicKey }
      );

      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: isHomeDelivery ? "forex_home_delivery" : "forex_branch_pickup",
          item_name: isHomeDelivery ? "Forex Home Delivery Order" : "Forex Branch Pickup Order",
          currency: data.currencyCode,
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Forex delivery order error:", err);
      toast({
        title: "Submission failed",
        description: "Something went wrong. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-xl shadow-md">
            <Package className="w-4 h-4 mr-2" />
            Order Foreign Currency
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Package className="w-5 h-5 text-orange-500" />
            Order Foreign Currency
          </DialogTitle>
        </DialogHeader>

        {/* ── Success state ── */}
        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
            <h3 className="text-2xl font-bold text-gray-900">Order Submitted!</h3>
            <p className="text-gray-600 max-w-sm">
              {isHomeDelivery
                ? "Your home delivery request has been received. Our forex team will contact you within 24 hours to confirm."
                : "Your branch pickup request has been received. Our team will confirm your slot within 24 hours."}
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-left w-full max-w-sm">
              <p className="text-sm font-semibold text-orange-800 mb-2">Order Summary</p>
              <div className="space-y-1 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Currency</span>
                  <span className="font-medium">{currencyAmountNum.toLocaleString("en-IN")} {watchedCode}</span>
                </div>
                <div className="flex justify-between">
                  <span>INR Equivalent</span>
                  <span className="font-medium">₹{formatINR(inrEquivalent)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{isHomeDelivery ? "Delivery Charge" : "Pickup Charge"}</span>
                  <span className="font-medium">{isHomeDelivery ? `₹${deliveryCharge}` : "Free"}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST</span>
                  <span className="font-medium">₹{formatINR(gstCharge)}</span>
                </div>
                <div className="flex justify-between border-t border-orange-200 pt-1 mt-1 font-bold text-orange-800">
                  <span>Total Payable</span>
                  <span>₹{formatINR(totalAmount)}</span>
                </div>
              </div>
            </div>
            <Button
              onClick={() => setOpen(false)}
              className="bg-orange-500 hover:bg-orange-600 text-white mt-2"
            >
              Close
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

              {/* ── Step indicator ── */}
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${step >= 1 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-500"}`}>1</div>
                <div className={`flex-1 h-1 rounded ${step >= 2 ? "bg-orange-500" : "bg-gray-200"}`} />
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${step >= 2 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-500"}`}>2</div>
                {needsDocs && (
                  <>
                    <div className={`flex-1 h-1 rounded ${step >= 3 ? "bg-orange-500" : "bg-gray-200"}`} />
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${step >= 3 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-500"}`}>3</div>
                  </>
                )}
                <span className="text-xs text-gray-500 ml-1">
                  {step === 1 ? "Currency & Fulfillment" : step === 2 ? (isHomeDelivery ? "Delivery Details" : "Pickup Details") : "Documents"}
                </span>
              </div>

              {/* ── Step 1: Fulfillment type + Currency & Amount ── */}
              {step === 1 && (
                <div className="space-y-5">

                  {/* Fulfillment type toggle */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">How would you like to receive your currency?</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleFulfillmentChange("home_delivery")}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          isHomeDelivery
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        <Home className="w-5 h-5" />
                        <span className="text-sm font-semibold">Home Delivery</span>
                        <span className="text-xs text-center leading-tight">Delhi NCR only (Noida, Gurgaon & more)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleFulfillmentChange("branch_pickup")}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          !isHomeDelivery
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        <MapPin className="w-5 h-5" />
                        <span className="text-sm font-semibold">Branch Pickup</span>
                        <span className="text-xs text-center leading-tight">Pick up from our office — Free</span>
                      </button>
                    </div>
                  </div>

                  {/* Branch selector — always required */}
                  <FormField
                    control={form.control}
                    name="branchLocation"
                    render={({ field }) => {
                      const selectedBranch = BRANCHES.find((b) => b.id === field.value);
                      return (
                        <FormItem>
                          <FormLabel>
                            {isHomeDelivery ? "Dispatching Branch *" : "Pickup Branch *"}
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a Travel Wings branch" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {BRANCHES.map((b) => (
                                <SelectItem key={b.id} value={b.id} textValue={b.name}>
                                  <div className="py-0.5">
                                    <p className="font-medium text-sm">{b.name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{b.address}</p>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedBranch && (
                            <p className="text-xs text-gray-500 flex items-start gap-1 mt-1">
                              <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-orange-400" />
                              {selectedBranch.address}
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  {/* Currency + Amount */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="currencyCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Foreign Currency</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select currency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {CURRENCIES.map((c) => (
                                <SelectItem key={c.code} value={c.code}>
                                  {c.code} – {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="currencyAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Amount ({watchedCode || "Currency"})</FormLabel>
                          <FormControl>
                            <Input type="number" min="1" step="any" placeholder="e.g. 500" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Order breakdown */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Order Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Exchange Rate</span>
                        <span className="font-medium">
                          {exchangeRate > 0
                            ? `1 ${watchedCode} = ₹${exchangeRate.toFixed(4)}`
                            : watchedCode ? "Rate not available" : "—"}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>INR Equivalent</span>
                        <span className="font-medium">₹{formatINR(inrEquivalent)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>{isHomeDelivery ? "Delivery Charge" : "Pickup Charge"}</span>
                        <span className="font-medium">
                          {!isHomeDelivery
                            ? <span className="text-green-600 font-semibold">Free</span>
                            : deliveryCharge > 0 ? `₹${deliveryCharge}` : "—"}
                        </span>
                      </div>
                      {isHomeDelivery && inrEquivalent > 0 && (
                        <p className="text-xs text-gray-400">
                          {inrEquivalent <= 50000
                            ? "Orders up to ₹50,000 attract ₹250 delivery charge."
                            : "Orders above ₹50,000 attract ₹150 delivery charge."}
                        </p>
                      )}
                      <div className="flex justify-between text-gray-600">
                        <span>GST (on forex conversion)</span>
                        <span className="font-medium">{gstCharge > 0 ? `₹${formatINR(gstCharge)}` : "—"}</span>
                      </div>
                      {inrEquivalent > 0 && (
                        <p className="text-xs text-gray-400">
                          {inrEquivalent <= 25000
                            ? "GST: ₹45 flat (orders up to ₹25,000)"
                            : inrEquivalent <= 100000
                            ? "GST: 0.18% of INR equivalent"
                            : "GST: ₹180 + 0.09% of INR equivalent"}
                        </p>
                      )}
                      <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 mt-1">
                        <span>Total Payable</span>
                        <span className="text-orange-600">₹{formatINR(totalAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleContinue}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  >
                    Continue to {isHomeDelivery ? "Delivery" : "Pickup"} Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* ── Step 2: Contact + Delivery / Pickup Details ── */}
              {step === 2 && (
                <div className="space-y-5">
                  {/* Order summary strip */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm space-y-1.5">
                    <div className="flex flex-wrap gap-4 items-center">
                      <span className="text-orange-800 font-semibold">{currencyAmountNum.toLocaleString("en-IN")} {watchedCode}</span>
                      <span className="text-gray-500">→ ₹{formatINR(inrEquivalent)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {isHomeDelivery
                        ? <span>+ ₹{deliveryCharge} delivery</span>
                        : <span className="text-green-600 font-medium">Free Pickup</span>}
                      {gstCharge > 0 && <span>+ ₹{formatINR(gstCharge)} GST</span>}
                    </div>
                    <div className="flex justify-between font-bold text-orange-700 border-t border-orange-200 pt-1.5 mt-0.5">
                      <span>Total Payable</span>
                      <span>₹{formatINR(totalAmount)}</span>
                    </div>
                  </div>

                  {/* Contact fields — always shown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Rahul Sharma" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="mobile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mobile Number *</FormLabel>
                          <FormControl>
                            <Input placeholder="10-digit number" maxLength={10} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="you@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* ── Home Delivery fields ── */}
                  {isHomeDelivery && (
                    <>
                      <FormField
                        control={form.control}
                        name="deliveryAddress"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Delivery Address *</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="House / Flat No., Street, Locality, Landmark"
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City *</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. New Delhi" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="state"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>State *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select state" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {INDIAN_STATES.map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="pinCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>PIN Code *</FormLabel>
                              <FormControl>
                                <Input placeholder="6-digit PIN" maxLength={6} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </>
                  )}

                  {/* Date + Purpose — always shown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="preferredDeliveryDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred {isHomeDelivery ? "Delivery" : "Pickup"} Date *</FormLabel>
                          <FormControl>
                            <Input type="date" min={minDate} max={maxDate} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="purpose"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Purpose of Exchange</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select purpose (optional)" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {PURPOSE_OPTIONS.map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes / Remarks (optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Any special instructions or additional notes"
                            rows={2}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={handleStep2Continue}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting…
                        </>
                      ) : needsDocs ? (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Continue to Documents
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          {isHomeDelivery ? <Truck className="w-4 h-4 mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                          Submit Order
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* ── Step 3: Document Upload ── */}
              {step === 3 && (
                <div className="space-y-5">
                  {/* Summary strip */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm space-y-1.5">
                    <div className="flex flex-wrap gap-4 items-center">
                      <span className="text-orange-800 font-semibold">{currencyAmountNum.toLocaleString("en-IN")} {watchedCode}</span>
                      <span className="text-gray-500">→ ₹{formatINR(inrEquivalent)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {isHomeDelivery
                        ? <span>+ ₹{deliveryCharge} delivery</span>
                        : <span className="text-green-600 font-medium">Free Pickup</span>}
                      {gstCharge > 0 && <span>+ ₹{formatINR(gstCharge)} GST</span>}
                    </div>
                    <div className="flex justify-between font-bold text-orange-700 border-t border-orange-200 pt-1.5 mt-0.5">
                      <span>Total Payable</span>
                      <span>₹{formatINR(totalAmount)}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-blue-800 mb-1">Documents Required — {watchedPurpose}</p>
                    <p className="text-xs text-blue-600">Please upload clear, readable copies. Accepted formats: JPG, PNG, PDF (max 10 MB each).</p>
                  </div>

                  <div className="space-y-3">
                    {requiredDocs.map((doc) => {
                      const uploaded = uploadedDocs[doc.id];
                      const isUploading = uploadingDoc === doc.id;
                      return (
                        <div key={doc.id} className={`border rounded-lg p-3 transition-colors ${uploaded ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              {doc.label}
                              {doc.required && <span className="text-red-500 ml-1">*</span>}
                            </span>
                            {uploaded && (
                              <span className="text-xs text-green-700 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Uploaded
                              </span>
                            )}
                          </div>
                          {uploaded ? (
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <FileText className="w-3 h-3 text-green-600 shrink-0" />
                              <span className="truncate">{uploaded.filename}</span>
                              <button
                                type="button"
                                onClick={() => setUploadedDocs((prev) => { const n = { ...prev }; delete n[doc.id]; return n; })}
                                className="ml-auto text-gray-400 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,application/pdf"
                                disabled={isUploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDocumentUpload(doc.id, file);
                                  e.target.value = "";
                                }}
                                className="text-xs text-gray-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer w-full"
                              />
                              {isUploading && <Loader2 className="w-4 h-4 animate-spin text-orange-500 shrink-0" />}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1" disabled={isSubmitting || !!uploadingDoc}>
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        const missing = requiredDocs.filter((d) => d.required && !uploadedDocs[d.id]);
                        if (missing.length > 0) {
                          toast({
                            title: "Documents missing",
                            description: `Please upload: ${missing.map((d) => d.label).join(", ")}`,
                            variant: "destructive",
                          });
                          return;
                        }
                        form.handleSubmit(onSubmit)();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                      disabled={isSubmitting || !!uploadingDoc}
                    >
                      {isSubmitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>
                      ) : (
                        <>{isHomeDelivery ? <Truck className="w-4 h-4 mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}Submit Order</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
